import LoadingButton from "@mui/lab/LoadingButton";
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography
} from "@mui/material";
import { FormEvent, useState } from "react";
import { Employee } from "../../models";
import { useData } from "../../utils/useData";
import { NavBackButton } from "./NavBackButton";
import { SideDrawerTitle } from "../shared/SideDrawer";
import { toTitleCase } from "../../utils/toTitleCase";
import { useBulkUnassignAccounts } from "../../hooks/employeeAccounts/useBulkUnassignAccounts";

type UnAssignToPlanProps = {
  handleComplete: () => void;
  handleClose: () => void;
  selectedEmployees: Employee[];
  shouldHideBackButton?: boolean;
  preselectPlanIds?: string[]
};

export const UnAssignPlans = ({
                                handleComplete,
                                handleClose,
                                selectedEmployees,
                                shouldHideBackButton = false,
                                preselectPlanIds = []
                              }: UnAssignToPlanProps) => {
  const { plans, accounts } = useData();

  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>(preselectPlanIds);
  const [error, setError] = useState("");

  const selectedEmployeesWithPlan = selectedEmployees.filter(
    (employee) => employee.accounts.length !== 0
  );

  const bulkUnassignAccounts = useBulkUnassignAccounts({
    onSuccess: () => {
      handleComplete();
      handleClose();
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });


  if (accounts.isLoading) {
    return <>Loading...</>;
  }

  const accountsWithSelectedPlans = accounts.data!.items
    .filter(account => selectedPlanIds.includes(account.planId))
    .filter(account => selectedEmployees.find((employee) => account.employeeId === employee.id));

  const canSelectedPlansBeUnAssigned = () => {
    if (accountsWithSelectedPlans.length === 0) {
      throw new Error(
        "None of the selected employees are assigned the plan(s) that were selected to un-assign."
      );
    }

    if (plans.data?.items.find(plan => selectedPlanIds.includes(plan.id))?.employerModuleOptions.employerModuleType === "PUBLIC_TRANSPORT") {
      throw new Error(
        "Public Transport module plans cannot be un-assigned."
      );
    }
  };

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    canSelectedPlansBeUnAssigned();
    bulkUnassignAccounts.mutate({
      selectedEmployeeIds: selectedEmployeesWithPlan.map(employee => employee.id),
      selectedPlanIds
    });
  }

  const totalAmountToBeRemoved = accountsWithSelectedPlans
    .map(x => x.availableBalance.amount)
    .reduce((sum, current) => sum + current, 0);

  return (
    <>
      {!shouldHideBackButton && <NavBackButton onBackPress={handleClose} />}
      <Box component={"form"} onSubmit={handleSubmit}>
        <Box display="flex" justifyContent="space-between">
          <SideDrawerTitle sx={{ paddingLeft: 2}}>
            Un-Assign {selectedEmployeesWithPlan.length} user
            {selectedEmployeesWithPlan.length > 1 ? "s" : ""} account(s)
          </SideDrawerTitle>
        </Box>
        <Box padding={2}>
          <Grid container spacing={2}>
            <Grid item>
              <Typography>
                General instructions on un-assigning plans
              </Typography>
              <Typography color="gray">
                All instances of selected plans will be un-assigned. This means the remaining funds be removed from the
                user's accounts
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={2} my={1}>
            <Grid item xs={12}>
              <Typography my={1}>Accounts to Un-Assign</Typography>
              <Autocomplete
                size="small"
                multiple
                options={(plans.data?.items ?? []).sort((a, b) => a.employerModuleOptions.employerModuleType.localeCompare(b.employerModuleOptions.employerModuleType))}
                getOptionLabel={(option) => option.name}
                value={plans.data?.items.filter((plan) =>
                  selectedPlanIds.includes(plan.id)
                )}
                groupBy={(option) => toTitleCase(option.employerModuleOptions.employerModuleType)}
                onChange={(_, newValue) => {
                  let newIds = newValue.map((plan) => plan.id);
                  setSelectedPlanIds(newIds);
                }}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField {...params} placeholder="Accounts to Un-Assign" />
                )}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        paddingLeft: 1,
                        paddingTop: 1,
                        fontSize: 14
                      }}
                    >
                      {params.group}
                    </Typography>
                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                      {params.children}
                    </ul>
                  </li>
                )}    
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} my={1}>
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
            {accountsWithSelectedPlans.length > 0 &&
              <Grid item>
                <Typography>
                  {accountsWithSelectedPlans.length} accounts will be removed. Totals of {totalAmountToBeRemoved}$ to be
                  removed from the user's accounts.
                </Typography>
              </Grid>}
          </Grid>
        </Box>
        <Box padding={2}>
          <LoadingButton type="submit" variant="contained" color="primary" fullWidth>
            Save
          </LoadingButton>
        </Box>
      </Box>
    </>
  );
};
